import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import {
  Download, Trash2, Star, StarOff, Edit3, Save, ChevronDown, ChevronUp,
  BarChart2, Activity, Shield, TrendingUp, Building2, CheckCircle2, XCircle, AlertCircle, Loader2,
} from 'lucide-react';
import { AppLayout, PageBreadcrumb } from '@/components/AppLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { StatusBadge } from '@/components/StatusBadge';
import { LivePriceBadge } from '@/components/LivePriceBadge';
import { MetricsTable } from '@/components/MetricsTable';
import { MetricTrendChart } from '@/components/charts/MetricTrendChart';
import { MarginChart } from '@/components/charts/MarginChart';
import { ScoreRadarChart } from '@/components/charts/ScoreRadarChart';
import { BenchmarkChart } from '@/components/charts/BenchmarkChart';
import { useEvaluation } from '@/hooks/useEvaluations';
import { useAppSettings } from '@/hooks/useAppSettings';
import { useSaveEvaluation, useDeleteEvaluation } from '@/hooks/useEvaluations';
import { useWatchlistItem, useAddToWatchlist, useRemoveFromWatchlist } from '@/hooks/useWatchlist';
import { exportAsText, exportAsMarkdown, exportAsHtml, downloadReport } from '@/lib/exportReport';
import { formatCurrency, formatPercent, formatNumber } from '@/lib/analysisEngine';
import type { EvaluationCriteria } from '@/types/stock';
import { cn } from '@/lib/utils';

function CriteriaRow({ criterion }: { criterion: EvaluationCriteria }) {
  const [expanded, setExpanded] = useState(false);
  const Icon = criterion.passed === true ? CheckCircle2 : criterion.passed === false ? XCircle : AlertCircle;
  const colorClass = criterion.passed === true
    ? 'text-emerald-600 dark:text-emerald-400'
    : criterion.passed === false
    ? 'text-red-500 dark:text-red-400'
    : 'text-amber-500';

  return (
    <div className={cn(
      'border rounded-lg overflow-hidden',
      criterion.passed === true ? 'border-emerald-200 dark:border-emerald-800/50' :
      criterion.passed === false ? 'border-red-200 dark:border-red-800/50' :
      'border-border',
    )}>
      <button
        className="w-full flex items-center gap-3 p-3 text-left hover:bg-muted/30 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <Icon className={cn('w-4 h-4 shrink-0', colorClass)} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium">{criterion.label}</p>
          <p className="text-xs text-muted-foreground">
            Value: {criterion.value !== null ? criterion.value.toFixed(2) : 'N/A'} | Target: {criterion.target}
          </p>
        </div>
        <StatusBadge
          status={criterion.passed === true ? 'PASS' : criterion.passed === false ? 'FAIL' : 'PENDING'}
          size="sm"
        />
        {expanded ? <ChevronUp className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />}
      </button>
      {expanded && (
        <div className="px-4 pb-3 pt-1 text-xs text-muted-foreground bg-muted/20 border-t border-border">
          {criterion.description}
        </div>
      )}
    </div>
  );
}

export default function EvaluationPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: report, isLoading } = useEvaluation(id);
  const { mutateAsync: saveEvaluation, isPending: isSaving } = useSaveEvaluation();
  const { mutateAsync: deleteEvaluation, isPending: isDeleting } = useDeleteEvaluation();
  const { data: watchlistItem } = useWatchlistItem(report?.ticker ?? '');
  const { mutateAsync: addToWatchlist } = useAddToWatchlist();
  const { mutateAsync: removeFromWatchlist } = useRemoveFromWatchlist();
  const { data: appSettings } = useAppSettings();

  const [editingComments, setEditingComments] = useState(false);
  const [comments, setComments] = useState('');

  useSeoMeta({
    title: report ? `${report.ticker} Analysis — Stock Fundamentals` : 'Loading…',
    description: report ? `Fundamental analysis for ${report.companyName} (${report.overallStatus})` : '',
  });

  if (isLoading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </AppLayout>
    );
  }

  if (!report) {
    return (
      <AppLayout>
        <div className="max-w-screen-lg mx-auto px-4 py-12 text-center">
          <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Evaluation Not Found</h2>
          <p className="text-muted-foreground mb-4">This evaluation may have been deleted.</p>
          <Button onClick={() => navigate('/')}>Go Home</Button>
        </div>
      </AppLayout>
    );
  }

  const handleSaveComments = async () => {
    await saveEvaluation({ ...report, comments });
    setEditingComments(false);
  };

  const handleDelete = async () => {
    if (confirm(`Delete evaluation for ${report.ticker}?`)) {
      await deleteEvaluation(report.id);
      navigate('/');
    }
  };

  const handleToggleWatchlist = async () => {
    if (watchlistItem) {
      await removeFromWatchlist(report.ticker);
    } else {
      await addToWatchlist({
        ticker: report.ticker,
        companyName: report.companyName,
        exchange: report.exchange,
        addedAt: Math.floor(Date.now() / 1000),
        lastEvaluationId: report.id,
        lastEvaluationDate: report.evaluationDate,
        lastEvaluationStatus: report.overallStatus,
      });
    }
  };

  const scoreColor = report.score >= 70 ? 'text-emerald-600' : report.score >= 40 ? 'text-amber-600' : 'text-red-600';

  return (
    <AppLayout>
      <div className="max-w-screen-xl mx-auto px-4 py-6">
        <PageBreadcrumb items={[
          { label: 'Dashboard', href: '/' },
          { label: report.ticker },
        ]} />

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start gap-4 mb-6">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-3xl font-bold font-mono">{report.ticker}</h1>
              <StatusBadge status={report.overallStatus} size="lg" />
              <Badge variant="outline">{report.sectorAnalysis.gicsSector}</Badge>
              <Badge variant="outline" className="capitalize">{report.sectorAnalysis.classification}</Badge>
            </div>
            <p className="text-muted-foreground mt-1">
              {report.companyName} · {report.exchange} · {report.analysisPeriod.startYear}–{report.analysisPeriod.endYear}
            </p>
            <div className="mt-1.5">
              <LivePriceBadge ticker={report.ticker} />
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Evaluated {new Date(report.evaluationDate * 1000).toLocaleDateString()} · Source: {report.dataSource.replace('_', ' ')}
            </p>
          </div>

          {/* Score */}
          <div className="flex items-center gap-2">
            <div className="text-center px-4 py-2 border rounded-lg bg-card">
              <p className={cn('text-3xl font-bold font-mono', scoreColor)}>{report.score}</p>
              <p className="text-xs text-muted-foreground">/ 100</p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleToggleWatchlist}
              className="gap-1.5"
            >
              {watchlistItem ? (
                <><StarOff className="w-3.5 h-3.5" /> Unwatch</>
              ) : (
                <><Star className="w-3.5 h-3.5" /> Watch</>
              )}
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-1.5">
                  <Download className="w-3.5 h-3.5" /> Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => downloadReport(exportAsText(report), `${report.ticker}_analysis.txt`, 'text/plain')}>
                  Plain Text (.txt)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => downloadReport(exportAsMarkdown(report), `${report.ticker}_analysis.md`, 'text/markdown')}>
                  Markdown (.md)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => downloadReport(exportAsHtml(report), `${report.ticker}_analysis.html`, 'text/html')}>
                  HTML Report (.html)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Button
              variant="ghost"
              size="sm"
              className="text-destructive hover:text-destructive gap-1.5"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[
            {
              label: 'Avg Net Margin',
              value: formatPercent(
                report.annualMetrics.reduce((s, m) => s + (m.profitMargin ?? 0), 0) / report.annualMetrics.length,
              ),
              icon: Activity,
            },
            {
              label: 'Avg Gross Margin',
              value: formatPercent(
                report.annualMetrics.reduce((s, m) => s + (m.grossMargin ?? 0), 0) / report.annualMetrics.length,
              ),
              icon: TrendingUp,
            },
            {
              label: 'Avg Current Ratio',
              value: formatNumber(
                report.annualMetrics.reduce((s, m) => s + (m.currentRatio ?? 0), 0) / report.annualMetrics.filter(m => m.currentRatio !== null).length,
                2,
              ) + 'x',
              icon: Shield,
            },
            {
              label: 'Industry',
              value: report.sectorAnalysis.industry.slice(0, 20),
              icon: Building2,
            },
          ].map((item) => (
            <Card key={item.label} className="text-center">
              <CardContent className="py-4">
                <item.icon className="w-5 h-5 text-primary mx-auto mb-1" />
                <p className="text-lg font-bold font-mono">{item.value}</p>
                <p className="text-xs text-muted-foreground">{item.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Main Tabs */}
        <Tabs defaultValue="charts">
          <TabsList className="mb-4">
            <TabsTrigger value="charts" className="gap-1.5">
              <BarChart2 className="w-3.5 h-3.5" /> Charts
            </TabsTrigger>
            <TabsTrigger value="table">
              <span>📊</span> Data Table
            </TabsTrigger>
            <TabsTrigger value="criteria" className="gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5" /> Criteria
            </TabsTrigger>
            <TabsTrigger value="sector">
              <span>🏭</span> Sector
            </TabsTrigger>
            <TabsTrigger value="price" className="gap-1.5">
              <TrendingUp className="w-3.5 h-3.5" /> Price
            </TabsTrigger>
            <TabsTrigger value="notes" className="gap-1.5">
              <Edit3 className="w-3.5 h-3.5" /> Notes
            </TabsTrigger>
          </TabsList>

          {/* Charts Tab */}
          <TabsContent value="charts" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card>
                <CardContent className="pt-4 pb-2">
                  <MarginChart metrics={report.annualMetrics} profitMarginTarget={15} />
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 pb-2">
                  <MetricTrendChart
                    metrics={report.annualMetrics}
                    metric="revenue"
                    label="Revenue"
                    isCurrency
                    showGrowthLine
                    growthMetric="revenueGrowth"
                    color="#3b82f6"
                  />
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 pb-2">
                  <MetricTrendChart
                    metrics={report.annualMetrics}
                    metric="eps"
                    label="EPS (Diluted)"
                    isPercent={false}
                    showGrowthLine
                    growthMetric="epsGrowth"
                    color="#8b5cf6"
                  />
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 pb-2">
                  <MetricTrendChart
                    metrics={report.annualMetrics}
                    metric="freeCashFlow"
                    label="Free Cash Flow"
                    isCurrency
                    color="#10b981"
                    showGrowthLine
                    growthMetric="fcfGrowth"
                  />
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 pb-2">
                  <MetricTrendChart
                    metrics={report.annualMetrics}
                    metric="currentRatio"
                    label="Current Ratio"
                    referenceValue={1.0}
                    referenceLabel="Target 1.0x"
                    color="#f59e0b"
                  />
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 pb-2">
                  <ScoreRadarChart criteria={report.criteria} />
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Table Tab */}
          <TabsContent value="table">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Annual Financial Metrics</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <MetricsTable metrics={report.annualMetrics} />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Criteria Tab */}
          <TabsContent value="criteria" className="space-y-3">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-1">
                <p className="text-sm text-muted-foreground">
                  {report.criteria.filter((c) => c.passed === true).length} passed ·{' '}
                  {report.criteria.filter((c) => c.passed === false).length} failed ·{' '}
                  {report.criteria.filter((c) => c.passed === null).length} N/A
                </p>
              </div>
            </div>
            <div className="space-y-2">
              {report.criteria.map((c, i) => <CriteriaRow key={i} criterion={c} />)}
            </div>
          </TabsContent>

          {/* Sector Tab */}
          <TabsContent value="sector">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Building2 className="w-4 h-4" /> Sector Classification
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {[
                    ['GICS Sector', report.sectorAnalysis.gicsSector],
                    ['GICS Code', report.sectorAnalysis.gicsCode],
                    ['Industry', report.sectorAnalysis.industry],
                    ['Classification', report.sectorAnalysis.classification],
                  ].map(([label, value]) => (
                    <div key={label} className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{label}</span>
                      <span className="font-medium capitalize">{value}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Competitive Moat Indicators</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {report.sectorAnalysis.moatIndicators.map((indicator, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <span className="text-primary mt-0.5 shrink-0">▸</span>
                        <span>{indicator}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
              {report.overview && (
                <Card className="sm:col-span-2">
                  <CardHeader>
                    <CardTitle className="text-base">Company Overview</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      {[
                        ['Market Cap', formatCurrency(report.overview.marketCap)],
                        ['P/E Ratio', report.overview.peRatio !== null ? formatNumber(report.overview.peRatio, 1) + 'x' : 'N/A'],
                        ['Beta', report.overview.beta !== null ? formatNumber(report.overview.beta, 2) : 'N/A'],
                        ['Div. Yield', report.overview.dividendYield !== null ? formatPercent(report.overview.dividendYield * 100) : 'N/A'],
                      ].map(([label, value]) => (
                        <div key={label} className="text-center p-3 bg-muted/30 rounded-lg">
                          <p className="text-base font-bold font-mono">{value}</p>
                          <p className="text-xs text-muted-foreground">{label}</p>
                        </div>
                      ))}
                    </div>
                    {report.overview.description && (
                      <p className="text-sm text-muted-foreground leading-relaxed line-clamp-4">
                        {report.overview.description}
                      </p>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Price Tab */}
          <TabsContent value="price" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card className="lg:col-span-2">
                <CardContent className="pt-4 pb-2">
                  <BenchmarkChart
                    ticker={report.ticker}
                    benchmarkSymbol={appSettings?.benchmarkSymbol ?? 'SPY'}
                    range="5y"
                    height={320}
                  />
                </CardContent>
              </Card>
              <Card className="lg:col-span-2">
                <CardContent className="pt-4 pb-2">
                  <BenchmarkChart
                    ticker={report.ticker}
                    benchmarkSymbol={appSettings?.benchmarkSymbol ?? 'SPY'}
                    range="10y"
                    height={280}
                  />
                </CardContent>
              </Card>
            </div>
            <p className="text-xs text-muted-foreground text-center">
              Price data from Yahoo Finance (public API, no key required). Prices are delayed ~15 minutes.
              Benchmark: {appSettings?.benchmarkSymbol ?? 'SPY'} — change in Settings.
            </p>
          </TabsContent>

          {/* Notes Tab */}
          <TabsContent value="notes">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Analysis Notes</CardTitle>
                  {!editingComments ? (
                    <Button variant="outline" size="sm" onClick={() => {
                      setComments(report.comments);
                      setEditingComments(true);
                    }}>
                      <Edit3 className="w-3.5 h-3.5 mr-1" /> Edit
                    </Button>
                  ) : (
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => setEditingComments(false)}>Cancel</Button>
                      <Button size="sm" onClick={handleSaveComments} disabled={isSaving}>
                        {isSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />}
                        Save
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {editingComments ? (
                  <Textarea
                    value={comments}
                    onChange={(e) => setComments(e.target.value)}
                    rows={12}
                    placeholder="Add your qualitative analysis notes here. Markdown is supported."
                    className="font-mono text-sm"
                  />
                ) : (
                  <div className="prose prose-sm dark:prose-invert max-w-none">
                    {report.comments ? (
                      <pre className="whitespace-pre-wrap font-sans text-sm text-foreground bg-muted/30 p-4 rounded-lg">
                        {report.comments}
                      </pre>
                    ) : (
                      <p className="text-muted-foreground italic text-sm">No notes yet. Click Edit to add your analysis.</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AppLayout>
  );
}
